import React, { useState, useEffect } from 'react'
import {
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  Stack,
  Link
} from '@mui/material'
import {
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material'
import { apiClient } from '../services/api'
import { formatToLongDate } from '../utils/textUtils'

interface ExceptionDetail {
  report_type: string
  name_short: string
  exception_count: number
  health_score?: number
  health_band: 'Good' | 'Watch' | 'Poor' | 'Critical' | 'Not Configured'
  severity: 'critical' | 'warning' | 'info'
  inspector_url?: string
}

interface DomainScoreDetails {
  domain: string
  band: 'Good' | 'Watch' | 'Poor' | 'Critical' | 'Not Configured'
  score: number | null
  total_exceptions: number
  exception_details: ExceptionDetail[]
  last_calculated: string
  score_methodology: string
}

interface HealthScoreTooltipProps {
  fp_code: string
  domain: 'product' | 'sale' | 'system'
  children: React.ReactElement
}

const HealthScoreTooltip: React.FC<HealthScoreTooltipProps> = ({ fp_code, domain, children }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<DomainScoreDetails | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [hasLoaded, setHasLoaded] = useState(false)

  const fetchScoreDetails = async () => {
    if (hasLoaded || loading) return // Prevent duplicate requests
    
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.get(`/admin/health-scores/${fp_code}/domain/${domain}`)
      setData(response.data)
      setHasLoaded(true)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch score details')
    } finally {
      setLoading(false)
    }
  }

  const handleTooltipOpen = () => {
    fetchScoreDetails()
  }

  const formatNumber = (num: number): string => {
    return num.toLocaleString()
  }

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon sx={{ fontSize: 16, color: '#f44336' }} />
      case 'warning':
        return <WarningIcon sx={{ fontSize: 16, color: '#ff9800' }} />
      case 'info':
        return <InfoIcon sx={{ fontSize: 16, color: '#2196f3' }} />
      default:
        return <SuccessIcon sx={{ fontSize: 16, color: '#4caf50' }} />
    }
  }

  const getSeverityColor = (severity: string): string => {
    switch (severity) {
      case 'critical': return '#f44336'
      case 'warning': return '#ff9800'
      case 'info': return '#2196f3'
      default: return '#4caf50'
    }
  }

  const getHealthBandColor = (band: string): string => {
    switch (band) {
      case 'Good': return '#4caf50'
      case 'Watch': return '#ff9800'
      case 'Poor': return '#f44336'
      case 'Critical': return '#212121'
      default: return '#9e9e9e'
    }
  }

  const getDomainDisplayName = (domain: string): string => {
    switch (domain) {
      case 'product': return '📦 Products'
      case 'sale': return '🧾 Sales'
      case 'system': return '🔧 System'
      default: return domain
    }
  }

  const renderTooltipContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading score details...</Typography>
        </Box>
      )
    }

    if (error) {
      return (
        <Box sx={{ p: 1, maxWidth: 250 }}>
          <Typography variant="body2" color="error">
            Error loading score details: {error}
          </Typography>
        </Box>
      )
    }

    if (!data) {
      return (
        <Typography variant="body2" sx={{ p: 1 }}>
          Hover to load score details
        </Typography>
      )
    }

    return (
      <Box sx={{ p: 2, maxWidth: 400 }}>
        {/* Header with domain and score */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {getDomainDisplayName(data.domain)} Health Score
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h6" color="primary">
              {data.score !== null ? `${Math.round(data.score)}/100` : 'Not Configured'}
            </Typography>
            <Chip
              label={data.band}
              size="small"
              sx={{
                backgroundColor: data.band === 'Good' ? '#4caf50' : 
                               data.band === 'Watch' ? '#ff9800' : 
                               data.band === 'Poor' ? '#f44336' : 
                               data.band === 'Critical' ? '#212121' : '#9e9e9e',
                color: 'white',
                fontWeight: 'bold'
              }}
            />
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Exception breakdown */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Exception Reports ({formatNumber(data.total_exceptions)} total)
          </Typography>
          <Stack spacing={1}>
            {data.exception_details.length > 0 ? (
              data.exception_details.map((exception, index) => (
                <Stack key={index} direction="row" alignItems="center" spacing={1}>
                  <Chip
                    label={exception.health_band}
                    size="small"
                    sx={{
                      backgroundColor: getHealthBandColor(exception.health_band),
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.7rem',
                      minWidth: '60px'
                    }}
                  />
                  <Box sx={{ flex: 1 }}>
                    {exception.inspector_url ? (
                      <Link
                        href={exception.inspector_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ 
                          textDecoration: 'none',
                          color: 'inherit',
                          display: 'block',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                      >
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold', mb: 0.5, color: '#007f98' }}>
                          {exception.name_short}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {formatNumber(exception.exception_count)} exceptions
                        </Typography>
                      </Link>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 'bold', mb: 0.5, color: '#007f98' }}>
                          {exception.name_short}
                        </Typography>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                          {formatNumber(exception.exception_count)} exceptions
                        </Typography>
                      </>
                    )}
                  </Box>
                </Stack>
              ))
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No exceptions found
              </Typography>
            )}
          </Stack>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Score calculation info */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            Score Calculation
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {data.score_methodology}
          </Typography>
        </Box>

        {/* Footer with last calculated */}
        <Divider sx={{ mt: 2, mb: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Last calculated: {formatToLongDate(data.last_calculated)}
        </Typography>
      </Box>
    )
  }

  // Don't show tooltip if data has been loaded and score is null/not configured
  const shouldShowTooltip = !data || data.score !== null

  if (!shouldShowTooltip) {
    // Return children without tooltip wrapper when there's no score
    return children
  }

  return (
    <Tooltip
      title={renderTooltipContent()}
      placement="bottom-start"
      arrow
      onOpen={handleTooltipOpen}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'background.paper',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3,
            maxWidth: 'none'
          }
        },
        arrow: {
          sx: {
            color: 'background.paper',
            '&::before': {
              border: '1px solid',
              borderColor: 'divider'
            }
          }
        }
      }}
    >
      {children}
    </Tooltip>
  )
}

export default HealthScoreTooltip